import React, { useState } from 'react'
import {
  Modal,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  Descriptions,
  Tag,
  message
} from 'antd'
import {
  CheckCircleOutlined,
  UserOutlined,
  BuildOutlined,
  CreditCardOutlined,
  CheckOutlined
} from '@ant-design/icons'
import { Lead } from '../services/api'

const { Option } = Select
const { TextArea } = Input

interface ConvertLeadToBusinessProps {
  visible: boolean
  lead: Lead
  onCancel: () => void
  onComplete: (businessData: {
    name: string
    email?: string
    phone?: string
    subscriptionPlan?: string
    userLimit?: number
  }) => Promise<void>
}

const ConvertLeadToBusiness: React.FC<ConvertLeadToBusinessProps> = ({
  visible,
  lead,
  onCancel,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const steps = [
    {
      title: 'Review Lead',
      icon: <UserOutlined />
    },
    {
      title: 'Business Details',
      icon: <BuildOutlined />
    },
    {
      title: 'Subscription',
      icon: <CreditCardOutlined />
    },
    {
      title: 'Confirm',
      icon: <CheckOutlined />
    }
  ]

  const handleNext = async () => {
    if (currentStep === 0) {
      // Step 1: Just review, no validation needed
      setCurrentStep(1)
      // Pre-fill form with lead data
      form.setFieldsValue({
        name: lead.firstName && lead.lastName
          ? `${lead.firstName} ${lead.lastName}`.trim()
          : lead.email.split('@')[0],
        email: lead.email,
        phone: lead.phone || ''
      })
    } else if (currentStep === 1) {
      // Step 2: Validate business details
      try {
        const values = await form.validateFields(['name', 'email', 'phone'])
        setCurrentStep(2)
      } catch (error) {
        console.error('Validation failed:', error)
      }
    } else if (currentStep === 2) {
      // Step 3: Validate subscription
      try {
        const values = await form.validateFields(['subscriptionPlan', 'userLimit'])
        setCurrentStep(3)
      } catch (error) {
        console.error('Validation failed:', error)
      }
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleComplete = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)
      await onComplete({
        name: values.name,
        email: values.email,
        phone: values.phone || undefined,
        subscriptionPlan: values.subscriptionPlan || 'starter',
        userLimit: values.userLimit || 3
      })
      message.success('Lead converted to business successfully')
      form.resetFields()
      setCurrentStep(0)
    } catch (error: any) {
      console.error('Conversion failed:', error)
      if (error.errorFields) {
        // Form validation error
        return
      }
      message.error('Failed to convert lead to business')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setCurrentStep(0)
    onCancel()
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card>
            <Descriptions title="Lead Information" column={2} bordered>
              <Descriptions.Item label="Name">
                {lead.firstName || lead.lastName
                  ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{lead.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{lead.phone || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="blue">{lead.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag color="orange">{lead.priority}</Tag>
              </Descriptions.Item>
              {lead.message && (
                <Descriptions.Item label="Message" span={2}>
                  {lead.message}
                </Descriptions.Item>
              )}
            </Descriptions>
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
              <p style={{ margin: 0 }}>
                <strong>Note:</strong> This will create a new business account and link it to this lead.
                The lead status will be updated to "converted".
              </p>
            </div>
          </Card>
        )

      case 1:
        return (
          <Card>
            <Form form={form} layout="vertical">
              <Form.Item
                name="name"
                label="Business Name"
                rules={[{ required: true, message: 'Business name is required' }]}
              >
                <Input placeholder="Enter business name" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Business Email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="Enter business email" />
              </Form.Item>

              <Form.Item name="phone" label="Business Phone">
                <Input placeholder="Enter business phone" />
              </Form.Item>
            </Form>
          </Card>
        )

      case 2:
        return (
          <Card>
            <Form form={form} layout="vertical">
              <Form.Item
                name="subscriptionPlan"
                label="Subscription Plan"
                initialValue="starter"
                rules={[{ required: true, message: 'Subscription plan is required' }]}
              >
                <Select>
                  <Option value="starter">Starter - $75.99/month</Option>
                  <Option value="professional">Professional - $149.99/month</Option>
                  <Option value="enterprise">Enterprise - Custom Pricing</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="userLimit"
                label="User Limit"
                initialValue={3}
                rules={[{ required: true, message: 'User limit is required' }]}
              >
                <Input type="number" min={1} placeholder="Number of users" />
              </Form.Item>
            </Form>
          </Card>
        )

      case 3:
        const formValues = form.getFieldsValue()
        return (
          <Card>
            <Descriptions title="Review Business Details" column={1} bordered>
              <Descriptions.Item label="Business Name">{formValues.name}</Descriptions.Item>
              <Descriptions.Item label="Email">{formValues.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{formValues.phone || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Subscription Plan">
                <Tag color="blue">{formValues.subscriptionPlan || 'starter'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="User Limit">{formValues.userLimit || 3}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '4px' }}>
              <p style={{ margin: 0 }}>
                <strong>Ready to convert:</strong> Click "Complete Conversion" to create the business account.
              </p>
            </div>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <Modal
      title="Convert Lead to Business"
      open={visible}
      onCancel={handleCancel}
      width={700}
      footer={null}
    >
      <Steps current={currentStep} style={{ marginBottom: '24px' }}>
        {steps.map((step, index) => (
          <Steps.Step key={index} title={step.title} icon={step.icon} />
        ))}
      </Steps>

      <div style={{ minHeight: '300px', marginBottom: '24px' }}>
        {renderStepContent()}
      </div>

      <div style={{ textAlign: 'right' }}>
        <Space>
          {currentStep > 0 && (
            <Button onClick={handlePrev}>Previous</Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={loading}
              onClick={handleComplete}
            >
              Complete Conversion
            </Button>
          )}
          <Button onClick={handleCancel}>Cancel</Button>
        </Space>
      </div>
    </Modal>
  )
}

export default ConvertLeadToBusiness

